/**
 * @file ContractDrawerStatusStripView.tsx
 * @description Pure presentation for the drawer/page status strip that
 *   replaces `ContractDrawerSavedHeader` (SE-4347 AC-7). Renders a
 *   green "Extracted" badge on the left, a "Saved {savedDateLabel}"
 *   label (when provided), and an outline "Edit" button on the right
 *   (when `showEditButton`).
 *
 *   **This file is strictly presentational** — all decision logic
 *   (whether to render the strip, date formatting, Edit-button gating)
 *   lives in `ContractDrawerStatusStripContainer`. The View receives
 *   resolved primitive props and renders them verbatim.
 *
 *   The outer view keeps the `contract-drawer-saved-header` testID
 *   wrapper and the `contract-drawer-edit-btn` testID on the button
 *   so existing e2e specs (`contracts-drawer.spec.ts`) continue to
 *   resolve selectors without modification.
 * @module features/contracts/components/ContractDrawerStatusStrip/ContractDrawerStatusStripView
 */

import { Pencil } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for {@link ContractDrawerStatusStripView}. */
export interface ContractDrawerStatusStripViewProps {
  /**
   * Pre-formatted saved date string (e.g. "6/16/2024"), or null when
   * the saved date is unknown / invalid. The container is responsible
   * for the formatting + null handling.
   */
  readonly savedDateLabel: string | null;
  /**
   * Whether the Edit button should render. Resolved by the container
   * from `isReadOnly` + permissions + edit-mode flags.
   */
  readonly showEditButton: boolean;
  /** Invoked when the user taps the Edit button. */
  readonly onEditPress: () => void;
  /**
   * Optional slot rendered immediately to the LEFT of the Edit button
   * on the strip's right side. Used by the drawer body (SE-4767) to
   * inline the Save and Discard buttons on the same horizontal row as
   * the Extracted badge + Edit button instead of stacking them
   * vertically beneath. The slot is wrapped in its own HStack so callers
   * pass either a single button or a fragment of multiple buttons.
   */
  readonly extraActions?: React.ReactNode;
}

/**
 * Pure presentation for the status strip.
 * @param props - View props
 * @param props.savedDateLabel - Pre-formatted date string (or null)
 * @param props.showEditButton - Whether to render the Edit button
 * @param props.onEditPress - Handler for the Edit button
 * @param props.extraActions - Optional slot to inline additional action
 *   buttons (e.g. Save, Discard) on the same row as Edit
 * @returns The strip
 */
const ContractDrawerStatusStripView = ({
  savedDateLabel,
  showEditButton,
  onEditPress,
  extraActions,
}: ContractDrawerStatusStripViewProps) => (
  <Box
    testID="contract-drawer-status-strip"
    /* Legacy testID preserved so existing e2e specs that locate
     * `contract-drawer-saved-header` continue to resolve. The outer
     * Box carries both so either selector works. */
  >
    <Box testID="contract-drawer-saved-header">
      <Stack
        direction="horizontal"
        className="items-center justify-between px-4 py-3"
      >
        <Stack direction="horizontal" className="items-center gap-3">
          <Box testID="contract-drawer-extracted-badge">
            <Badge size="sm" action="success" variant="solid">
              <BadgeText>Extracted</BadgeText>
            </Badge>
          </Box>
          {savedDateLabel !== null ? (
            <Text variant="body" className="text-content-muted">
              Saved {savedDateLabel}
            </Text>
          ) : null}
        </Stack>
        <Stack direction="horizontal" className="items-center gap-2">
          {extraActions}
          {showEditButton ? (
            <Button
              testID="contract-drawer-edit-btn"
              label="Edit"
              size="sm"
              variant="outline"
              tone="primary"
              icon={Pencil}
              onPress={onEditPress}
            />
          ) : null}
        </Stack>
      </Stack>
    </Box>
  </Box>
);

ContractDrawerStatusStripView.displayName = "ContractDrawerStatusStripView";

export default memo(ContractDrawerStatusStripView);
